import { Navigate, Outlet } from 'react-router';
import { RoleNames } from '../../config/role-names';
import { useSubscription } from '../../hooks/subscription/use-subscription';
import { usePermissions } from '../../hooks/use-permissions';
import { isGuardQuerySettling } from './guard-query-state';

interface PrepackagedLabelAddonGuardProps {
  redirectTo?: string;
}

/**
 * Route guard for the prepackaged-label area (`/labels`). Checks
 * `SubscriptionInfo.prepackagedLabelEnabled`, the same tenant flag the backend
 * PrepackagedLabelAddonGuard enforces on every label API — gating on the
 * nutrition add-on instead let the page open and then fail with 403. The
 * Consulente bypass mirrors the backend (the consultant curates labels on the
 * tenant's behalf regardless of the per-tenant toggle).
 *
 * Gates on `isPending`, never `isLoading`: both queries are disabled until the
 * tenant is validated, and a disabled query reports `isLoading: false` with no
 * data — reading it as "settled" redirected a cold `/labels` load to /home
 * (same trap as `protected-route.helpers.ts`).
 */
export function PrepackagedLabelAddonGuard({
  redirectTo = '/home',
}: PrepackagedLabelAddonGuardProps) {
  const { prepackagedLabelEnabled, isPending, fetchStatus } = useSubscription();
  const {
    roleName,
    isPending: permissionsPending,
    fetchStatus: permissionsFetchStatus,
  } = usePermissions();
  if (
    isGuardQuerySettling({ isPending, fetchStatus }) ||
    isGuardQuerySettling({ isPending: permissionsPending, fetchStatus: permissionsFetchStatus })
  ) {
    return null;
  }
  if (roleName === RoleNames.CONSULTANT) {
    return <Outlet />;
  }
  if (!prepackagedLabelEnabled) {
    return <Navigate to={redirectTo} replace />;
  }
  return <Outlet />;
}
